import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Theme = 'blue' | 'green' | 'purple' | 'orange' | 'teal' | 'windows' | 'mac' | 'linux' | 'office';

export interface ThemeColors {
  light: {
    primary: string;
    secondary: string;
    background: string;
    surface: string;
    text: string;
    textSecondary: string;
    border: string;
    accent: string;
  };
  dark: {
    primary: string;
    secondary: string;
    background: string;
    surface: string;
    text: string;
    textSecondary: string;
    border: string;
    accent: string;
  };
}

export const themeDefinitions: Record<Theme, ThemeColors> = {
  blue: {
    light: {
      primary: '#0061a4',
      secondary: '#565f71',
      background: '#fdfbff',
      surface: '#fdfbff',
      text: '#1a1c1e',
      textSecondary: '#43474e',
      border: '#c3c7cf',
      accent: '#0061a4'
    },
    dark: {
      primary: '#9eca00',
      secondary: '#bec6dc',
      background: '#0f1419',
      surface: '#0f1419',
      text: '#e1e2e5',
      textSecondary: '#c3c7cf',
      border: '#43474e',
      accent: '#9eca00'
    }
  },
  green: {
    light: {
      primary: '#146c2e',
      secondary: '#52634f',
      background: '#f9fdec',
      surface: '#f9fdec',
      text: '#0f1f11',
      textSecondary: '#3e4e3c',
      border: '#becfbb',
      accent: '#146c2e'
    },
    dark: {
      primary: '#7fdb6a',
      secondary: '#b9ccb1',
      background: '#0a1308',
      surface: '#0a1308',
      text: '#e4f1de',
      textSecondary: '#becfbb',
      border: '#3e4e3c',
      accent: '#7fdb6a'
    }
  },
  purple: {
    light: {
      primary: '#6b46c1',
      secondary: '#5c5f61',
      background: '#fef7ff',
      surface: '#fef7ff',
      text: '#1d1a22',
      textSecondary: '#49454e',
      border: '#cac4d0',
      accent: '#6b46c1'
    },
    dark: {
      primary: '#cfbcff',
      secondary: '#c1c9c2',
      background: '#141218',
      surface: '#141218',
      text: '#e6e1e9',
      textSecondary: '#cac4d0',
      border: '#49454e',
      accent: '#cfbcff'
    }
  },
  orange: {
    light: {
      primary: '#8b5000',
      secondary: '#5f5f5f',
      background: '#fffbf7',
      surface: '#fffbf7',
      text: '#231a00',
      textSecondary: '#4e4639',
      border: '#d4c7b6',
      accent: '#8b5000'
    },
    dark: {
      primary: '#ffb945',
      secondary: '#c1c9c2',
      background: '#1f1600',
      surface: '#1f1600',
      text: '#f0e2c0',
      textSecondary: '#d4c7b6',
      border: '#4e4639',
      accent: '#ffb945'
    }
  },
  teal: {
    light: {
      primary: '#006a6a',
      secondary: '#4a6363',
      background: '#f3fffe',
      surface: '#f3fffe',
      text: '#051f1f',
      textSecondary: '#3d4e4e',
      border: '#bcd3d2',
      accent: '#006a6a'
    },
    dark: {
      primary: '#4dd0e1',
      secondary: '#aec9c8',
      background: '#001f20',
      surface: '#001f20',
      text: '#d7f5f6',
      textSecondary: '#bcd3d2',
      border: '#3d4e4e',
      accent: '#4dd0e1'
    }
  },
  windows: {
    light: {
      primary: '#0078d4',
      secondary: '#5c5f61',
      background: '#ffffff',
      surface: '#ffffff',
      text: '#323130',
      textSecondary: '#605e5c',
      border: '#edebe9',
      accent: '#0078d4'
    },
    dark: {
      primary: '#60cdff',
      secondary: '#c8c6c4',
      background: '#0f0f0f',
      surface: '#1f1f1f',
      text: '#ffffff',
      textSecondary: '#a19f9d',
      border: '#3b3a39',
      accent: '#60cdff'
    }
  },
  mac: {
    light: {
      primary: '#007aff',
      secondary: '#8e8e93',
      background: '#f5f5f7',
      surface: '#ffffff',
      text: '#1d1d1f',
      textSecondary: '#86868b',
      border: '#d1d1d6',
      accent: '#007aff'
    },
    dark: {
      primary: '#0a84ff',
      secondary: '#98989d',
      background: '#000000',
      surface: '#1c1c1e',
      text: '#ffffff',
      textSecondary: '#98989d',
      border: '#38383a',
      accent: '#0a84ff'
    }
  },
  linux: {
    light: {
      primary: '#2d5016',
      secondary: '#5c5f61',
      background: '#f7f9f7',
      surface: '#ffffff',
      text: '#1b1b1b',
      textSecondary: '#5c5f61',
      border: '#e0e0e0',
      accent: '#2d5016'
    },
    dark: {
      primary: '#4ade80',
      secondary: '#9ca3af',
      background: '#0f172a',
      surface: '#1e293b',
      text: '#f1f5f9',
      textSecondary: '#94a3b8',
      border: '#334155',
      accent: '#4ade80'
    }
  },
  office: {
    light: {
      primary: '#0078d4',
      secondary: '#5c5f61',
      background: '#ffffff',
      surface: '#ffffff',
      text: '#323130',
      textSecondary: '#605e5c',
      border: '#edebe9',
      accent: '#0078d4'
    },
    dark: {
      primary: '#0078d4',
      secondary: '#c8c6c4',
      background: '#0f0f0f',
      surface: '#1f1f1f',
      text: '#ffffff',
      textSecondary: '#a19f9d',
      border: '#3b3a39',
      accent: '#0078d4'
    }
  }
};

interface ThemeContextType {
  theme: Theme;
  mode: 'light' | 'dark';
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
  setMode: (mode: 'light' | 'dark') => void;
  themeColors: ThemeColors['light'] | ThemeColors['dark'];
  availableThemes: Theme[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    return {
      theme: 'blue' as Theme,
      mode: 'dark' as const,
      toggleTheme: () => {},
      setTheme: () => {},
      setMode: () => {},
      themeColors: themeDefinitions.blue.dark,
      availableThemes: Object.keys(themeDefinitions) as Theme[],
    };
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    const savedTheme = localStorage.getItem('astravision_theme') as Theme;
    if (savedTheme && savedTheme in themeDefinitions) {
      return savedTheme;
    }
    return 'blue'; // Default to blue theme
  });

  const [mode, setModeState] = useState<'light' | 'dark'>(() => {
    const savedMode = localStorage.getItem('astravision_mode') as 'light' | 'dark';
    if (savedMode === 'light' || savedMode === 'dark') {
      return savedMode;
    }
    return 'dark'; // Default to dark enterprise theme
  });

  const themeColors = themeDefinitions[theme][mode];
  const availableThemes: Theme[] = Object.keys(themeDefinitions) as Theme[];

  // Force light theme on login page only
  const isLoginPage = window.location.pathname === '/login';
  const effectiveMode = isLoginPage ? 'light' : mode;
  const effectiveThemeColors = themeDefinitions[theme][effectiveMode];

  useEffect(() => {
    // Apply theme to document
    const root = document.documentElement;

    // Remove all theme classes
    Object.keys(themeDefinitions).forEach(themeName => {
      root.classList.remove(themeName);
      root.classList.remove(`${themeName}-light`);
      root.classList.remove(`${themeName}-dark`);
    });

    // Add current theme and mode classes
    root.classList.add(theme);
    root.classList.add(`${theme}-${effectiveMode}`);

    if (effectiveMode === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.remove('dark');
      root.classList.add('light');
    }

    // Set CSS custom properties for theme colors
    Object.entries(effectiveThemeColors).forEach(([key, value]) => {
      root.style.setProperty(`--theme-${key}`, value);
    });

    // Save to localStorage
    localStorage.setItem('astravision_theme', theme);
    localStorage.setItem('astravision_mode', mode);
  }, [theme, mode, effectiveThemeColors, effectiveMode]);

  const toggleTheme = () => {
    setModeState(prevMode => {
      const nextMode = prevMode === 'light' ? 'dark' : 'light';
      try { localStorage.setItem('astravision_mode', nextMode); } catch {}
      return nextMode;
    });
  };

  const setMode = (newMode: 'light' | 'dark') => {
    try { localStorage.setItem('astravision_mode', newMode); } catch {}
    setModeState(newMode);
  };

  const setTheme = (newTheme: Theme) => {
    if (newTheme in themeDefinitions) {
      try { localStorage.setItem('astravision_theme', newTheme); } catch {}
      setThemeState(newTheme);
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        mode: effectiveMode,
        toggleTheme,
        setTheme,
        setMode,
        themeColors: effectiveThemeColors,
        availableThemes,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
